// web/src/lib/image-resize.ts
//
// Client-side image compression — runs in the browser before upload to S3.
// Shipped 2026-05-26.
//
// Why: phone cameras default to 3-12 MB per photo. We were storing every
// pixel as-shot and paying CloudFront egress every time someone scrolled
// past the album. For an archive that displays at most ~800px wide on a
// retina phone (= ~1600 device pixels), there's no point storing 4032×3024.
//
// What it does:
//   - Reads the user's File via Image bitmap
//   - Resizes proportionally so the LONGEST edge is ≤ MAX_DIMENSION (2048px)
//   - Re-encodes as WebP at quality 0.82 (WebP is universally supported in
//     modern browsers; quality 0.82 is visually indistinguishable from
//     JPEG quality 0.92 at ~half the bytes)
//   - Falls back to JPEG if WebP encoding isn't supported (unlikely)
//   - Returns a new File with .webp/.jpg suffix and the same logical name
//
// What it does NOT do:
//   - Touch images that are ALREADY small (≤ 1.5 MB and ≤ MAX_DIMENSION):
//     no point burning a canvas pass on something that's already fine.
//   - Strip EXIF metadata explicitly — canvas re-encoding drops EXIF as a
//     side effect anyway, which is a privacy win (geolocation, device ID).
//   - Block uploads that fail to resize. We just pass through the original
//     file and let the server-side 10 MB cap reject if it's huge.
//
// Cost benefit: typical phone JPEG (4032×3024, 5 MB) → WebP (2048×1536,
// ~280 KB). That's an ~18x reduction. Across 50k Free users × 10 photos
// per season = saves ~2 TB/year of S3 + the same of CloudFront egress.

const MAX_DIMENSION = 2048;
const WEBP_QUALITY = 0.82;
const JPEG_FALLBACK_QUALITY = 0.85;
// Skip resize for files already this small (these are usually screenshots
// or already-compressed photos; re-encoding only loses quality).
const SKIP_THRESHOLD_BYTES = 1.5 * 1024 * 1024;

/**
 * Compress an image File for upload. Returns a NEW File (or the original
 * if already small enough). Never throws — falls back to the original on
 * any pipeline error.
 *
 * @param file The user-selected File from an <input type="file">
 * @returns Promise resolving to a (usually smaller) File
 */
export async function compressForUpload(file: File): Promise<File> {
  // Bail early if not an image at all (HEIC etc. are still images though).
  if (!file.type.startsWith('image/')) return file;

  // Skip already-small files. Cheaper to upload as-is than to spin up
  // canvas + re-encode for something that's already 800 KB.
  if (file.size <= SKIP_THRESHOLD_BYTES) {
    // BUT: still proceed if the image is dimensionally huge but byte-small
    // (e.g. a 5000×5000 1-colour PNG — small bytes, big dimensions). We
    // skip the cheap path only if we can't tell dimensions yet, so go
    // ahead and process.
  }

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = scaledDimensions(bitmap.width, bitmap.height);

    // If the image is already small in BOTH bytes and pixels, return as-is.
    if (file.size <= SKIP_THRESHOLD_BYTES && width === bitmap.width && height === bitmap.height) {
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    // Try WebP first.
    const webpBlob = await canvasToBlob(canvas, 'image/webp', WEBP_QUALITY);
    if (webpBlob && webpBlob.size < file.size) {
      return new File([webpBlob], renamedTo(file.name, 'webp'), {
        type: 'image/webp',
        lastModified: file.lastModified,
      });
    }

    // WebP not supported / didn't actually shrink → try JPEG.
    const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', JPEG_FALLBACK_QUALITY);
    if (jpegBlob && jpegBlob.size < file.size) {
      return new File([jpegBlob], renamedTo(file.name, 'jpg'), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    }

    // Both encoders failed or made things bigger → just upload the original.
    return file;
  } catch (err) {
    // Resize failed for any reason — log + pass through. The server-side
    // 10 MB content-length-range will reject anything massive anyway.
    console.warn('[image-resize] failed, falling back to original file:', err);
    return file;
  }
}

/** Load a File into an ImageBitmap (faster than HTMLImageElement). */
async function loadBitmap(file: File): Promise<ImageBitmap> {
  // createImageBitmap handles HEIC on iOS Safari ≥17 + every modern
  // browser's WebP/JPEG/PNG. If it fails (some old Android browsers),
  // the outer catch in compressForUpload handles it.
  return createImageBitmap(file);
}

/** Calculate proportional resize dimensions so the longest edge ≤ MAX_DIMENSION. */
function scaledDimensions(srcW: number, srcH: number): { width: number; height: number } {
  if (srcW <= MAX_DIMENSION && srcH <= MAX_DIMENSION) {
    return { width: srcW, height: srcH };
  }
  const ratio = Math.min(MAX_DIMENSION / srcW, MAX_DIMENSION / srcH);
  return {
    width: Math.round(srcW * ratio),
    height: Math.round(srcH * ratio),
  };
}

/** Promisified canvas.toBlob — null if the browser can't encode that type. */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

/** Rename "IMG_1234.jpg" → "IMG_1234.webp" (or .jpg). */
function renamedTo(name: string, ext: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return `${name}.${ext}`;
  return `${name.slice(0, dot)}.${ext}`;
}
